import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import {
  applyFinding,
  discardFinding,
  dismissFinding,
  discardRewrite,
  keepFinding,
  regenerate as queueRewrite,
  rerollObject,
  revertFinding,
  reviewLessonAgain,
  updateScene,
  switchMechanic,
} from '@platform/client';
import { playableScenes } from '@platform/schema/review';
import { device } from '@platform/tokens';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';
import { env } from '../env';
import { jobQuery, lessonReviewsQuery, reviewQuery } from '../library/sources';

import { castLine } from './cast';
import { ChatPanel } from './ChatPanel';
import { DeviceFrame, framedViewerUrl } from './DeviceFrame';
import { EditableText } from './EditableText';
import { useEditing } from './editing';
import {
  illustrationFrom,
  illustrationLine,
  illustrationNote,
} from './illustration';
import { jumpRefusal } from './jump';
import { LabelEditor } from './LabelEditor';
import { LanguageCheck } from './LanguageCheck';
import { LearnerFeedback } from './LearnerFeedback';
import { useLessonFixes } from './LessonFix';
import { MissingPictures } from './MissingPictures';
import { seedFor, teachingScenes } from './order';
import { COVER_COPY, previewCover } from './preview-cover';
import { PublishDialog } from './PublishDialog';
import { Regenerate } from './Regenerate';
import { handEditedNote, jobNote, jobSettled } from './regeneration';
import { ResearchPanel, ReviewBanner } from './ResearchPanel';
import { ReviewPanel } from './ReviewPanel';
import { RewriteChanges } from './RewriteChanges';
import { SaveState } from './SaveState';
import { RemoveScene, SceneArc } from './SceneArc';
import { SceneEditor } from './SceneEditor';
import { LessonCover } from './SceneImage';
import { Scorecard } from './Scorecard';
import { createSourceFrom } from './source';
import { SourceChecks } from './SourceChecks';
import { elapsedOf } from './stage';
import { StorySentences } from './StorySentences';
import { tagsQuery } from './tags';
import { UndoRedo } from './UndoRedo';
import { useNow } from './useNow';
import { Verified } from './Verified';
import { useViewerFrame } from './viewer-frame';
import { VisualFixFlow } from './VisualFix';

import type { PublishTarget } from './PublishDialog';
import type { RewriteRequest } from './regeneration';
import type { ArcChange } from '@platform/schema/chat';
import type { AuthorableKind } from '@platform/schema/content';

/**
 * The editor's floor, and why the row is allowed to scroll.
 *
 * Two of the three panes claim their width before the editor sees any: the preview is
 * fixed at `device.width`, and the chat floors at 14rem in `ChatPanel`. That leaves the
 * editor last in line, so without a floor of its own it absorbs every pixel the other
 * two refuse — it reached 90px at a 1024px window. Below roughly 1270px the three no
 * longer fit, and the row scrolls sideways rather than shrinking this past 21rem.
 */
const EDITOR_MIN_WIDTH = '21rem';

/**
 * The review workspace — spec 013 §11. Tree on the left, the lesson on the right.
 *
 * The right pane is an iframe of `apps/viewer`, which renders the real product components
 * under react-native-web. `004` §3.2 forbids the admin importing `packages/ui`, but the
 * reason is the stronger one: a second renderer of one schema drifts, and the question
 * this screen answers is *what will this look like*.
 *
 * **The admin fetches, the viewer renders.** §10.1 — the viewer holds no token and makes
 * no request; the payload arrives by `postMessage` once it says it is ready.
 */
export function ReviewWorkspace() {
  const { id = '' } = useParams<{ id: string }>();

  // Another lesson is a fresh workspace: the preview's cover, the viewer's readiness and
  // the scene in focus all describe one lesson, and none of them may outlive it.
  return <Workspace key={id} id={id} />;
}

function Workspace({ id }: { id: string }) {
  /**
   * Which question the pane is answering — GEN-80.
   *
   * `play` is the default because it is the one a reviewer cannot get anywhere else: the
   * lesson as the learner meets it, at phone size, answerable. `key` is the pane as it
   * was, every answer revealed at once, which is faster for *is the marked answer right*
   * and impossible to read as the product.
   */
  const [mode, setMode] = useState<'play' | 'key'>('play');

  /**
   * Which palette the preview draws in — GEN-94.
   *
   * `light` is the default because it is what this pane has always shown. The control
   * exists because dark was unreachable: the viewer hardcoded a scheme, so the one screen
   * built to judge how a lesson looks could not show half of the design language.
   */
  const [scheme, setScheme] = useState<'light' | 'dark'>('light');
  const queries = useQueryClient();
  const [jobId, setJobId] = useState<string>();

  /**
   * Which finding the job in flight belongs to, or none for a plain rewrite — GEN-76.
   *
   * One `jobId` still follows both, because both are one model turn ending in a write to
   * this lesson. What was missing is *who asked*: the progress line was written in the
   * rewrite's words at the top of the screen, so the answer to a click on a finding
   * appeared nowhere near the finding and said the wrong thing when it got there.
   */
  const [applying, setApplying] = useState<string>();

  // Polled while an import writes its module, so Publish unlocks when the module's does.
  const lesson = useQuery({
    ...reviewQuery(id),
    refetchInterval: (query) =>
      query.state.data?.writing === true ? 2_000 : false,
  });

  // Polled while a review of this lesson runs, which Fix with AI also queues — CURR-54.
  const reviews = useQuery({
    ...lessonReviewsQuery(id),
    refetchInterval: (query) =>
      query.state.data?.reviewing === undefined ? false : 2_000,
  });

  const fixes = useLessonFixes(id);
  const tags = useQuery(tagsQuery());
  const vocabulary = (tags.data?.data.tags ?? []).map((tag) => tag.slug);

  const payload = lesson.data;

  /** Which scenes the preview cannot play yet, and which it can — GEN-80, GEN-90. */
  const { unplayable, playable } = useMemo(() => {
    if (payload === undefined) {
      return { unplayable: [], playable: new Set<string>() };
    }

    const scenes = playableScenes(payload.scenes);

    return {
      unplayable: scenes.unfinished,
      // Widened off the branded `SceneId`, because the row it is asked about comes from
      // the SDK, where the same id is a plain string.
      playable: new Set<string>(scenes.playable.map((scene) => scene.id)),
    };
  }, [payload]);

  /**
   * Which scene the preview was last asked to open on, and how often — GEN-90.
   *
   * Counted rather than toggled: asking for the same scene twice has to open it twice,
   * and a payload that has not changed remounts nothing on the far side.
   */
  const [jump, setJump] = useState<{ sceneId: string; nonce: number }>();

  /**
   * The scene the planner is asked about — GEN-97. The last one jumped to or opened;
   * none is the whole lesson.
   */
  const [focused, setFocused] = useState<string>();
  /** The arc's Go to scene and a finding's Show — ADMIN-22. */
  const jumpTo = (sceneId: string) => {
    setFocused(sceneId);
    // The answer key has no run to open, so the ask is also what puts the pane back
    // into the mode that can honour it.
    setMode('play');
    setJump((previous) => ({ sceneId, nonce: (previous?.nonce ?? 0) + 1 }));
  };
  const usedIn = new Map(
    (payload?.usage ?? []).map((row) => [row.sceneId, row.lessons]),
  );

  // Every edit re-reads the lesson, which is also what re-renders the preview: the pane on
  // the right is a function of the payload, so a correction shows up there without the
  // form knowing the viewer exists.
  // By prefix, so the reviews list is read again too: an edit can put it out of date.
  const refresh = async (): Promise<void> => {
    await queries.invalidateQueries({ queryKey: reviewQuery(id).queryKey });
  };

  // One lesson here, several on the lessons screen — `useEditing` takes the set either
  // way, and the write names which of them it lands in.
  const inView = useMemo(
    () => (payload === undefined ? [] : [payload]),
    [payload],
  );

  const edit = useEditing(inView, refresh);

  // The three scene controls send the same write with a different change — GEN-77. Named
  // once so the shape is not restated at each of them.
  const changeArc = (change: ArcChange): void => {
    edit.save({ kind: 'arc', lessonId: id, change });
  };

  /**
   * Queued, not awaited. A regeneration is a job — §4.1 puts it at `HIGH` because somebody
   * is watching, but a planner turn is a minute and the console does not hold the screen
   * for it.
   *
   * The job id comes back so the screen can follow it. It used to be discarded, and the
   * reviewer was told to refresh at a moment nothing told them about.
   */
  const regenerate = useTrackedMutation('Rewrite', {
    mutationFn: async ({
      request,
      instruction,
    }: {
      request: RewriteRequest;
      instruction: string;
    }) => {
      // `lessonId` is added here and nowhere else: it is the placement being reviewed,
      // which decides whether the write forks, and a scene editor does not know it.
      const queued = await queueRewrite({
        body: {
          target: { ...request, lessonId: id },
          instruction,
        },
        throwOnError: true,
      });

      return queued.data.id;
    },
    onSuccess: (id) => {
      setApplying(undefined);
      setJobId(id);
    },
  });

  /**
   * Follow the job the click started, and stop the moment it settles.
   *
   * The console used to say "the new draft appears when the job finishes" and leave the
   * reviewer to guess when that was. Polling is enough here — one job, one screen, and a
   * planner turn is a minute — where a stream would be §11's refused complexity.
   */
  const job = useQuery({
    ...jobQuery(jobId ?? ''),
    enabled: jobId !== undefined,
    refetchInterval: (query) =>
      jobSettled(query.state.data?.status) ? false : 3000,
  });

  const status = job.data?.status;

  // A queued job is timed from the queueing, which is when the reviewer clicked.
  const now = useNow(jobId !== undefined && !jobSettled(status));
  const elapsed =
    job.data === undefined
      ? undefined
      : elapsedOf(
          {
            startedAt: job.data.startedAt ?? job.data.createdAt,
            finishedAt: job.data.finishedAt,
          },
          now,
        );

  // A finished review moves the findings and the scores, which the workspace read holds.
  const reviewing = reviews.data?.reviewing;
  const wasReviewing = useRef(reviewing);
  useEffect(() => {
    if (wasReviewing.current !== undefined && reviewing === undefined)
      void queries.invalidateQueries({
        queryKey: reviewQuery(id).queryKey,
        exact: true,
      });
    wasReviewing.current = reviewing;
  }, [reviewing, id, queries]);

  const reviewAgain = useTrackedMutation('Review again', {
    mutationFn: async () => {
      await reviewLessonAgain({ path: { id }, throwOnError: true });
    },
    onSettled: refresh,
  });

  // The refresh the reviewer was being asked to do by hand.
  //
  // The job id is kept rather than cleared: the settled status is what the note reads, and
  // dropping the id would take the answer off the screen at the moment it arrived.
  // `refetchInterval` has already stopped the polling.
  useEffect(() => {
    if (!jobSettled(status)) return;

    void queries.invalidateQueries({ queryKey: reviewQuery(id).queryKey });
  }, [status, id, queries]);

  /**
   * Put the previous version back — GEN-56.
   *
   * Addressed by generation row rather than by scene: the snapshot lives on the row, and
   * the row is the only thing that knows which rewrite this is undoing.
   */
  const discard = useTrackedMutation('Discard the rewrite', {
    mutationFn: async () => {
      const rowId = job.data?.rows[0]?.id;
      if (rowId === undefined) return;

      await discardRewrite({ path: { id: rowId }, throwOnError: true });
    },
    onSuccess: refresh,
  });

  /**
   * Ask the editor to answer one finding — SKILL-03.
   *
   * Queued, and followed through the same `jobId` a rewrite uses: both are one model turn
   * that ends in a write to this lesson, and the screen has one place to say so.
   */
  const apply = useTrackedMutation('Apply the finding', {
    mutationFn: async (findingId: string) => {
      setApplying(findingId);

      const queued = await applyFinding({
        path: { id: findingId },
        throwOnError: true,
      });

      return queued.data.id;
    },
    onSuccess: setJobId,
  });

  /**
   * Apply what Fix with AI proposed — GEN-76's keep, and since CURR-33 the write. Until it
   * is called the scene is unchanged and the finding still counts against a publish.
   */
  const keep = useTrackedMutation('Apply the fix', {
    mutationFn: async (findingId: string) => {
      await keepFinding({ path: { id: findingId }, throwOnError: true });
    },
    onSuccess: refresh,
  });

  /** Accept a finding without a fix — ADMIN-22. It counts as passed from here on. */
  const allow = useTrackedMutation('Allow the finding', {
    mutationFn: async (findingId: string) => {
      await dismissFinding({ path: { id: findingId }, throwOnError: true });
    },
    onSuccess: refresh,
  });

  /** Turn a proposal down — CURR-33. Nothing was written, so nothing is restored. */
  const discardFix = useTrackedMutation('Discard the fix', {
    mutationFn: async (findingId: string) => {
      await discardFinding({ path: { id: findingId }, throwOnError: true });
    },
    onSuccess: refresh,
  });

  /**
   * Undo one edit the editor made — SKILL-03.
   *
   * Addressed by finding rather than by scene, because the finding is what holds the copy
   * taken before the rewrite. The finding returns to `OPEN` server-side, so the blocker it
   * raised counts against publishing again.
   */
  const revert = useTrackedMutation('Revert the fix', {
    mutationFn: async (findingId: string) => {
      await revertFinding({ path: { id: findingId }, throwOnError: true });
    },
    onSuccess: refresh,
  });

  // Two subjects, one job. `applying` says which of them the line in flight is about, so
  // the rewrite's note stays above the fields and the editor's goes beside its finding.
  const note = jobNote(
    {
      sending: regenerate.isPending,
      failed: regenerate.isError,
      status: applying === undefined ? status : undefined,
      elapsed,
    },
    'rewrite',
  );

  const asked = jobNote(
    { sending: apply.isPending, failed: apply.isError, status, elapsed },
    'edit',
  );
  // Why it could not be asked, beside the finding rather than only in the action log.
  const editNote =
    asked !== undefined && apply.error !== null
      ? {
          ...asked,
          message: `${asked.message} (${describeApiError(apply.error)})`,
        }
      : asked;

  // What a whole-lesson rewrite would spare, and whether it would write anything at all.
  const spared = handEditedNote(
    payload?.handEdited.length ?? 0,
    payload?.scenes.length ?? 0,
  );

  // In flight from the click until the job settles, so a reviewer cannot queue a second
  // rewrite of a scene whose first one has not landed.
  const regenerating =
    regenerate.isPending || (jobId !== undefined && !jobSettled(status));

  const [publishing, setPublishing] =
    useState<Pick<PublishTarget, 'action' | 'why'>>();

  /**
   * Name where a claim came from — spec 013 §17, the step the console never had.
   *
   * Two writes because they are two rows: a source is content in its own right and may be
   * cited by more than one scene. `updateScene` gained `sourceId` for this; before it, a
   * generated CASE scene had no route to a source at all.
   */
  const ground = useTrackedMutation('Record a source', {
    mutationFn: async (request: { sceneId: string; input: string }) => {
      const sourceId = await createSourceFrom(
        request.input,
        payload?.title ?? 'Source',
      );
      if (sourceId === undefined) return;

      await updateScene({
        path: { id: request.sceneId },
        body: { sourceId },
        throwOnError: true,
      });
    },
    onSuccess: refresh,
  });

  /**
   * Change how an existing question is asked — GEN-78.
   *
   * A refresh rather than an optimistic edit, and not part of the undo stack: the write
   * clears the answer key and may fork a shared scene into a new row, so what the screen
   * held a moment ago is not what the lesson now points at. `undoStep` inverts a patch
   * against a scene it can still find, and after a fork it cannot.
   */
  const mechanic = useTrackedMutation('Change the mechanic', {
    mutationFn: async (request: { sceneId: string; kind: AuthorableKind }) => {
      await switchMechanic({
        path: { lessonId: id, sceneId: request.sceneId },
        body: { kind: request.kind },
        throwOnError: true,
      });
    },
    onSuccess: refresh,
  });

  /**
   * Match the illustration to the scenes — spec 013 §13, amended. There is no image vendor
   * in v1, so this is what "regenerate the art" means: `objectFor` re-read over the scenes
   * as they now stand. It is instant and it writes one column, so unlike a regeneration it
   * refreshes rather than queueing.
   *
   * The result is kept rather than discarded. Re-reading unchanged scenes writes the same
   * value back, and without the returned column the screen cannot tell that apart from a
   * click that did nothing — GEN-42.
   */
  const reroll = useTrackedMutation('Match the illustration', {
    mutationFn: async () => {
      // Read before the write. `onSuccess` refetches, so by the time the note renders the
      // payload already holds the new value and comparing against it would always match.
      const previous = payload?.objectName;
      const rerolled = await rerollObject({ path: { id }, throwOnError: true });

      return { previous, next: illustrationFrom(rerolled.data.objectName) };
    },
    onSuccess: refresh,
  });

  const illustration = illustrationNote({
    pending: reroll.isPending,
    failed: reroll.isError,
    // The mutation's own reading while it has one; the lesson's otherwise, which is the
    // case a refused write reports against.
    previous: reroll.data?.previous ?? payload?.objectName,
    next: reroll.data?.next,
  });

  const message = useMemo(
    () =>
      payload === undefined
        ? undefined
        : { type: 'lesson' as const, lesson: payload, mode, scheme, jump },
    [payload, mode, scheme, jump],
  );
  const { frame, ready: viewerReady } = useViewerFrame(message);

  const cover = previewCover({
    sceneCount: payload?.scenes.length,
    failed: lesson.isError,
    viewerReady,
  });

  return (
    // A scrollbar below ~1270px, rather than a crushed pane: this is a desktop
    // tool (004 §7), and `EDITOR_MIN_WIDTH` carries the arithmetic.
    <Stack
      direction="row"
      spacing={2}
      sx={{ height: '100%', overflowX: 'auto' }}
    >
      <Stack
        spacing={2}
        sx={{ flex: 2, minWidth: EDITOR_MIN_WIDTH, overflowY: 'auto' }}
      >
        {lesson.isPending && <LinearProgress />}

        {payload !== undefined && (
          <>
            <ReviewBanner lessonId={id} />

            {/* Where the work stands, and the way back out of it — GEN-55, GEN-43.
                Above the fields, because both answer questions about what is below. */}
            <Stack
              direction="row"
              spacing={2}
              sx={{ alignItems: 'center', flexWrap: 'wrap' }}
            >
              <SaveState />
              <UndoRedo
                undoLabel={edit.back?.label}
                redoLabel={edit.forward?.label}
                disabled={edit.pending || regenerating}
                onUndo={edit.undo}
                onRedo={edit.redo}
              />
              {/* Named for what it publishes, and locked while the module's own publish
                  is: a lesson published into a half-written module goes live alone. */}
              {payload.status === 'DRAFT' && (
                <>
                  <Button
                    variant="contained"
                    disabled={edit.pending || regenerating || payload.writing}
                    onClick={() => {
                      setPublishing({ action: 'publish' });
                    }}
                  >
                    Publish this lesson
                  </Button>
                  {payload.writing && (
                    <Typography variant="body2" color="text.secondary">
                      Publish unlocks once the lessons are written
                    </Typography>
                  )}
                </>
              )}
            </Stack>

            <PublishDialog
              target={
                publishing === undefined
                  ? null
                  : { ...publishing, lesson: id, name: payload.title }
              }
              onClose={() => {
                setPublishing(undefined);
              }}
              onDone={refresh}
            />

            {/* Not disabled while a save is in flight. Locking every field for the
                duration of somebody else's `PATCH` is how a correction typed during those
                few hundred milliseconds went nowhere — GEN-43. The writes are partial and
                address different columns, so two in flight do not race. */}
            <EditableText
              label="Title"
              value={payload.title}
              onSave={(title) => {
                edit.save({ kind: 'lesson', lessonId: id, body: { title } });
              }}
            />
            <EditableText
              label="Summary"
              value={payload.summary}
              multiline
              onSave={(summary) => {
                edit.save({ kind: 'lesson', lessonId: id, body: { summary } });
              }}
            />

            {payload.cast !== undefined && (
              <Typography variant="body2">
                Cast: {castLine(payload.cast)}
              </Typography>
            )}

            {/* Saved through the same autosave every other field uses, which is what
                stamps `handEditedAt` and so keeps the correction through the next
                regeneration — 016 §6. */}
            <LabelEditor
              personas={payload.personas}
              tags={payload.tags}
              vocabulary={vocabulary}
              onChange={(labels) => {
                edit.save({ kind: 'lesson', lessonId: id, body: labels });
              }}
            />

            <ResearchPanel lessonId={id} lessonTitle={payload.title} />

            <LessonCover url={payload.coverUrl} title={payload.title} />
            <MissingPictures
              lessonId={id}
              pictures={payload.pictures}
              onChanged={refresh}
            />

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {illustrationLine(payload.objectName)}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={reroll.isPending}
                onClick={() => {
                  reroll.mutate();
                }}
              >
                Match the scenes
              </Button>
            </Stack>
            {illustration !== undefined && (
              <Alert severity={illustration.severity}>
                {illustration.message}
              </Alert>
            )}

            {note !== undefined && (
              <Alert severity={note.severity}>{note.message}</Alert>
            )}

            {/* The five scores above the findings that produced them — GEN-89. The
                verdict is what a reviewer opens this screen to read; the findings below
                are why it says that. */}
            <Scorecard qa={payload.qa} />

            <Verified lessonId={id} />

            <LearnerFeedback lessonId={id} />

            {/* What the reviewer found, and what the editor changed — SKILL-03. Above
                the rewrite controls because a finding is the reason somebody would reach
                for one. */}
            <ReviewPanel
              reviews={reviews.data}
              findings={payload.findings}
              cards={{
                lessonId: id,
                fixes,
                scenes: payload.scenes,
                busy:
                  revert.isPending ||
                  keep.isPending ||
                  discardFix.isPending ||
                  allow.isPending ||
                  apply.isPending ||
                  regenerating,
                applying,
                note: editNote,
                onApply: (findingId) => {
                  apply.mutate(findingId);
                },
                onKeep: (findingId) => {
                  keep.mutate(findingId);
                },
                onDiscard: (findingId) => {
                  discardFix.mutate(findingId);
                },
                onRevert: (findingId) => {
                  revert.mutate(findingId);
                },
                onAllow: (findingId) => {
                  allow.mutate(findingId);
                },
                onShow: jumpTo,
                showRefused: (scene) =>
                  jumpRefusal(scene.kind, playable.has(scene.id)),
              }}
              attention={payload.qa.blockers + payload.qa.uncategorised > 0}
              asking={reviewAgain.isPending}
              onReviewAgain={() => {
                reviewAgain.mutate();
              }}
            />

            <StorySentences sentences={payload.storySentences} />

            <SourceChecks lessonId={id} review />

            {/* LANG-01. Beside the findings, because it is the other list of what to fix,
                and on opening: published text written before the standard is flagged
                here rather than rewritten. */}
            <LanguageCheck
              lessonId={id}
              lesson={payload}
              disabled={regenerating || edit.pending}
              onSave={edit.save}
            />

            {/* What the rewrite actually did — GEN-56. Only once the job has settled:
                the row carries no snapshot until the planner has come back. Not for an
                applied finding — GEN-76: that job's row holds the same edit, and two
                diffs of one change with two different undo buttons is worse than none. */}
            {applying === undefined && jobSettled(status) && (
              <RewriteChanges
                row={job.data?.rows[0]}
                disabled={discard.isPending}
                onDiscard={() => {
                  discard.mutate();
                }}
              />
            )}
            {regenerating && <LinearProgress />}

            {/* The whole lesson, written again — GEN-47. Above the arc because it acts
                on every scene below it, and a control that reaches further than the one
                beside it belongs further up. */}
            <Regenerate
              choices={[
                {
                  key: 'LESSON',
                  label: 'Regenerate every scene',
                  request: { targetType: 'LESSON' },
                },
              ]}
              warning={spared.message}
              disabled={regenerating || edit.pending || spared.blocked}
              onRegenerate={(choice, instruction) => {
                regenerate.mutate({ request: choice.request, instruction });
              }}
            />

            <SceneArc
              count={teachingScenes(payload.scenes)}
              disabled={edit.pending || regenerating}
              onMove={(from, to) => {
                const scene = payload.scenes[from];
                if (scene === undefined) return;

                changeArc({ op: 'move', sceneId: scene.id, position: to + 1 });
              }}
              onAdd={(sceneKind) => {
                changeArc({ op: 'add', scene: seedFor(sceneKind) });
              }}
            >
              {payload.scenes.map((scene) => (
                <SceneEditor
                  key={scene.id}
                  scene={scene}
                  cast={payload.cast}
                  usedIn={usedIn.get(scene.id) ?? 1}
                  disabled={regenerating || mechanic.isPending}
                  remove={
                    <RemoveScene
                      count={teachingScenes(payload.scenes)}
                      disabled={edit.pending}
                      onRemove={() => {
                        changeArc({ op: 'remove', sceneId: scene.id });
                      }}
                    />
                  }
                  fixVisual={
                    <VisualFixFlow
                      lessonId={id}
                      sceneId={scene.id}
                      disabled={regenerating || mechanic.isPending}
                    />
                  }
                  jumpRefused={jumpRefusal(scene.kind, playable.has(scene.id))}
                  onOpen={() => {
                    setFocused(scene.id);
                  }}
                  onImage={() => {
                    void refresh();
                  }}
                  onJump={() => {
                    jumpTo(scene.id);
                  }}
                  onScene={(body) => {
                    edit.save({
                      kind: 'scene',
                      lessonId: id,
                      sceneId: scene.id,
                      body,
                    });
                  }}
                  onMechanic={(kind) => {
                    mechanic.mutate({ sceneId: scene.id, kind });
                  }}
                  onFix={(field) =>
                    edit.fix({ lessonId: id, sceneId: scene.id, field })
                  }
                  onRevert={(fix) =>
                    edit.revert(
                      { lessonId: id, sceneId: scene.id, field: fix.field },
                      fix,
                    )
                  }
                  onQuestion={(questionId, explanation) => {
                    edit.save({
                      kind: 'question',
                      lessonId: id,
                      questionId,
                      explanation,
                    });
                  }}
                  onSource={(input) => {
                    ground.mutate({ sceneId: scene.id, input });
                  }}
                  onRegenerate={(choice, instruction) => {
                    regenerate.mutate({ request: choice.request, instruction });
                  }}
                />
              ))}
            </SceneArc>
          </>
        )}
      </Stack>

      {env.VITE_VIEWER_URL === undefined ? (
        <Alert
          severity="info"
          sx={{ flex: '0 0 auto', width: `${String(device.width)}px` }}
        >
          The preview is unavailable: VITE_VIEWER_URL is unset. Run{' '}
          <code>pnpm viewer</code> and reload. The scenes are listed on the
          left.
        </Alert>
      ) : (
        // A phone, at the size the product is designed against. The question this pane
        // answers is *what will this look like*, and a lesson stretched across a desktop
        // pane answers a question nobody asked — line lengths, wrapping and how much of a
        // scene is above the fold are all wrong at that width.
        //
        // So the pane is sized by the phone rather than given a share of the row. A
        // flexible pane spends the difference on gutter either side of a 390px frame,
        // and takes that width from the editor, which has nothing fixed about it.
        <Stack
          spacing={1}
          sx={{
            flex: '0 0 auto',
            alignItems: 'center',
            overflow: 'auto',
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              onChange={(_, next: 'play' | 'key' | null) => {
                // `null` is the group reporting the selected button pressed again. There
                // is no unset mode, so that is a no-op rather than an empty pane.
                if (next !== null) setMode(next);
              }}
              aria-label="What the preview shows"
            >
              <ToggleButton value="play">Play it</ToggleButton>
              <ToggleButton value="key">Answer key</ToggleButton>
            </ToggleButtonGroup>

            {/* Beside the mode rather than drawn inside the frame — GEN-94. Both say how
                to read what is below them, and neither is part of the lesson. */}
            <ToggleButtonGroup
              exclusive
              size="small"
              value={scheme}
              onChange={(_, next: 'light' | 'dark' | null) => {
                if (next !== null) setScheme(next);
              }}
              aria-label="Which theme the preview uses"
            >
              <ToggleButton value="light">Light</ToggleButton>
              <ToggleButton value="dark">Dark</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {/* Named beside the phone rather than drawn inside it. A half-finished scene
              has nothing to answer — `playableScenes` is `sceneSchema`, the same gate
              that decides what a learner may be served — and leaving it out of the run
              silently reads as a broken preview rather than as work still to do. */}
          {mode === 'play' && unplayable.length > 0 && (
            <Alert severity="info" sx={{ width: '100%' }}>
              Not in this run yet:{' '}
              {unplayable
                .map((scene) => `${String(scene.position)}. ${scene.prompt}`)
                .join(' · ')}
            </Alert>
          )}

          <DeviceFrame>
            <Box
              component="iframe"
              ref={frame}
              src={framedViewerUrl(env.VITE_VIEWER_URL)}
              title="Lesson preview"
              sx={{
                width: '100%',
                height: '100%',
                border: 0,
                display: 'block',
              }}
            />
            {cover !== undefined && (
              <Stack
                role="status"
                spacing={1}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  p: 3,
                  bgcolor: 'background.paper',
                }}
              >
                <Typography color="text.secondary">
                  {COVER_COPY[cover]}
                </Typography>
                {cover === 'failed' && (
                  <Button
                    size="small"
                    onClick={() => {
                      void lesson.refetch();
                    }}
                  >
                    Retry
                  </Button>
                )}
              </Stack>
            )}
          </DeviceFrame>
        </Stack>
      )}

      {/* Apply is `edit.save` and nothing else — GEN-66. A proposal is shaped like the
          write the form makes, so applying one lands in the same history, stamps the same
          `handEditedAt`, and is taken back by the same ⌘Z. */}
      <ChatPanel
        subject={{
          kind: 'lesson',
          lessonIds: [id],
          lessons: inView,
          onApply: edit.save,
          onApplyAll: edit.saveAll,
          focus:
            focused === undefined
              ? undefined
              : { lessonId: id, sceneId: focused },
          onUnfocus: () => {
            setFocused(undefined);
          },
        }}
        disabled={regenerating}
      />
    </Stack>
  );
}
